import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "ld_access_token";

export function setAccessToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}
export function getAccessToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,  // Include httpOnly cookie when browser allows
});

// Bearer-token fallback for browsers that block third-party cookies
// (Safari in iframes, private mode, etc.). Cookie remains the primary path;
// header only fills in when cookie isn't available.
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// If any protected endpoint returns 401 with a stale token, clear it so the
// browser doesn't keep sending an invalid header forever. /auth/me and
// /auth/login are opt-out — they handle their own null/error states.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/register") || url.includes("/auth/me") || url.includes("/auth/logout");
    if (status === 401 && !isAuthEndpoint && getAccessToken()) {
      setAccessToken(null);
    }
    return Promise.reject(err);
  }
);

// Bulletproof error formatter. NEVER returns null/undefined.
export function formatApiError(err) {
  const fallback = "Something went wrong. Please try again.";
  if (!err) return fallback;

  // Network / no response
  if (!err.response) {
    if (err.code === "ERR_NETWORK") return "Can't reach the server. Check your connection.";
    return err.message && err.message !== "null" ? err.message : fallback;
  }

  const d = err.response.data?.detail;
  if (d == null) return fallback;
  if (typeof d === "string") return d;

  // Pydantic 422 — array of { msg, loc, ... }. Prettify.
  if (Array.isArray(d)) {
    const messages = d
      .map((e) => {
        if (!e) return null;
        const field = Array.isArray(e.loc) ? e.loc[e.loc.length - 1] : "";
        let msg = typeof e.msg === "string" ? e.msg : "";
        msg = msg.replace(/^value is not a valid email address: .*$/i, "That doesn't look like a valid email address.");
        msg = msg.replace(/^string should have at least (\d+) characters$/i, "Must be at least $1 characters.");
        if (field && msg) return `${prettyField(field)}: ${msg}`;
        return msg || null;
      })
      .filter(Boolean);
    return messages.length ? messages.join(" ") : fallback;
  }

  if (typeof d?.msg === "string") return d.msg;
  return fallback;
}

function prettyField(f) {
  const map = { email: "Email", password: "Password", name: "Name", business_name: "Business name" };
  return map[f] || String(f);
}

export default api;
